/**
 * Attachments Routes (Hono + OpenAPI)
 *
 * Generic REST endpoints for file attachments across entities.
 * Two attach modes:
 *   POST .../files       — attach existing file by reference (JSON { fileId })
 *   POST .../files/upload — upload + attach (multipart, dedupe + internal visibility)
 */

import { OpenAPIHono, createRoute, z } from '@hono/zod-openapi'
import type { MiddlewareHandler } from 'hono'
import type { ModuleContext } from '@gzl10/nexus-sdk'
import type { NexusEnv, OpenApiHandler } from '../../http/types.js'
import { requireAbility } from '../../http/middleware/ability.middleware.js'
import type { Subjects } from '@kernel/abilities/ability.types.js'
import type { AttachmentsService } from './attachments.service.js'
import type { AttachableConfig } from './attachments.types.js'
import { getStorageService } from './storage.service.js'
import type { UploadedFile } from './storage.types.js'

export function createAttachmentsRoutes(ctx: ModuleContext) {
  const app = new OpenAPIHono<NexusEnv>()
  const attachmentsService = ctx.services.get<AttachmentsService>('attachments')

  const authMw = ctx.core.middleware['auth']
  if (!authMw) throw new Error('Auth middleware is required for attachments routes')
  const auth = authMw as unknown as MiddlewareHandler<NexusEnv>

  const canCreateAttachment = requireAbility(
    'create',
    'StorageFileAttachment' as Subjects
  ) as unknown as MiddlewareHandler<NexusEnv>
  const canDeleteAttachment = requireAbility(
    'delete',
    'StorageFileAttachment' as Subjects
  ) as unknown as MiddlewareHandler<NexusEnv>

  const anyObject = z.object({}).passthrough()

  const entityParams = z.object({ entityType: z.string(), entityId: z.string() })
  const entityFileParams = z.object({ entityType: z.string(), entityId: z.string(), fileId: z.string() })
  const fileIdParams = z.object({ fileId: z.string() })

  function getAuth(c: import('hono').Context<NexusEnv>) {
    return {
      userId: c.get('user')?.id,
      ability: c.get('ability')
    }
  }

  function resolveAttachableConfig(entityType: string): AttachableConfig | undefined {
    const dotIndex = entityType.indexOf('.')
    if (dotIndex === -1) return undefined
    const moduleName = entityType.slice(0, dotIndex)
    const entityName = entityType.slice(dotIndex + 1)
    const def = ctx.engine.getEntityDefinition(moduleName, entityName) as Record<string, unknown> | undefined
    if (!def) return undefined
    const attachable = def['attachable']
    if (!attachable || attachable === true) return undefined
    return attachable as AttachableConfig
  }

  // GET /entities/:entityType/:entityId/files
  const listFilesRoute = createRoute({
    method: 'get',
    path: '/entities/{entityType}/{entityId}/files',
    tags: ['Attachments'],
    summary: 'List files attached to an entity',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    request: { params: entityParams },
    responses: {
      200: { description: 'Attached files', content: { 'application/json': { schema: z.array(anyObject) } } }
    }
  })
  app.openapi(listFilesRoute, (async (c) => {
    const entityType = c.req.param('entityType')!; const entityId = c.req.param('entityId')!
    return c.json(await attachmentsService.getFilesForEntity(entityType, entityId))
  }) as OpenApiHandler)

  // PUT /entities/:entityType/:entityId/files — sync (replace all)
  const syncFilesRoute = createRoute({
    method: 'put',
    path: '/entities/{entityType}/{entityId}/files',
    tags: ['Attachments'],
    summary: 'Replace all attached files',
    security: [{ bearerAuth: [] }],
    middleware: [auth, canCreateAttachment] as const,
    request: {
      params: entityParams,
      body: { content: { 'application/json': { schema: z.object({ fileIds: z.array(z.string()) }) } } }
    },
    responses: {
      200: { description: 'Synced files', content: { 'application/json': { schema: z.array(anyObject) } } }
    }
  })
  app.openapi(syncFilesRoute, (async (c) => {
    const entityType = c.req.param('entityType')!; const entityId = c.req.param('entityId')!
    const { fileIds } = (await c.req.json()) as { fileIds: string[] }
    const cfg = resolveAttachableConfig(entityType)
    return c.json(await attachmentsService.syncEntityFiles(entityType, entityId, fileIds, getAuth(c), cfg))
  }) as OpenApiHandler)

  // POST /entities/:entityType/:entityId/files — attach existing by reference
  const attachByRefRoute = createRoute({
    method: 'post',
    path: '/entities/{entityType}/{entityId}/files',
    tags: ['Attachments'],
    summary: 'Attach an existing file by reference',
    security: [{ bearerAuth: [] }],
    middleware: [auth, canCreateAttachment] as const,
    request: {
      params: entityParams,
      body: { content: { 'application/json': { schema: z.object({ fileId: z.string() }) } } }
    },
    responses: {
      201: { description: 'Attached', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(attachByRefRoute, (async (c) => {
    const entityType = c.req.param('entityType')!; const entityId = c.req.param('entityId')!
    const { fileId } = (await c.req.json()) as { fileId: string }
    if (!fileId) throw new ctx.core.errors.ValidationError('fileId is required')

    const storageService = getStorageService()
    const fileRecord = await storageService.getById(fileId)
    if (!fileRecord) throw new ctx.core.errors.NotFoundError('File')

    const cfg = resolveAttachableConfig(entityType)
    await attachmentsService.attachEntityFile(entityType, entityId, fileId, getAuth(c), cfg, fileRecord.mimetype)
    return c.json({ success: true }, 201)
  }) as OpenApiHandler)

  // POST /entities/:entityType/:entityId/files/upload — upload + attach
  const uploadAttachRoute = createRoute({
    method: 'post',
    path: '/entities/{entityType}/{entityId}/files/upload',
    tags: ['Attachments'],
    summary: 'Upload and attach a file (dedupe + internal visibility)',
    security: [{ bearerAuth: [] }],
    middleware: [auth, canCreateAttachment] as const,
    request: {
      params: entityParams,
      body: {
        content: {
          'multipart/form-data': {
            schema: z.object({ file: z.any() })
          }
        }
      }
    },
    responses: {
      201: { description: 'Uploaded + attached', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(uploadAttachRoute, (async (c) => {
    const entityType = c.req.param('entityType')!; const entityId = c.req.param('entityId')!
    const body = await c.req.parseBody({ all: true })
    const fileRaw = body['file']
    const fileEntry = Array.isArray(fileRaw) ? fileRaw[0] : fileRaw
    if (!fileEntry || !(fileEntry instanceof File)) {
      throw new ctx.core.errors.ValidationError('File upload required')
    }

    const cfg = resolveAttachableConfig(entityType)

    // Validate access + constraints before uploading (avoid orphans on rejection)
    await attachmentsService.checkEntityAccess(entityType, entityId, getAuth(c))
    await attachmentsService.validateConstraints(entityType, entityId, cfg, fileEntry.type)

    const buf = Buffer.from(await fileEntry.arrayBuffer())
    const file: UploadedFile = {
      fieldname: 'file',
      originalname: fileEntry.name,
      mimetype: fileEntry.type || 'application/octet-stream',
      buffer: buf,
      size: fileEntry.size
    }
    const storageService = getStorageService()
    const result = await storageService.upload(file, {
      dedupe: true,
      visibility: 'internal',
      userId: getAuth(c).userId
    })

    await attachmentsService.attach(result.id, entityType, entityId)
    return c.json(result, 201)
  }) as OpenApiHandler)

  // DELETE /entities/:entityType/:entityId/files/:fileId
  const detachRoute = createRoute({
    method: 'delete',
    path: '/entities/{entityType}/{entityId}/files/{fileId}',
    tags: ['Attachments'],
    summary: 'Detach a file from an entity',
    security: [{ bearerAuth: [] }],
    middleware: [auth, canDeleteAttachment] as const,
    request: { params: entityFileParams },
    responses: {
      204: { description: 'Detached' }
    }
  })
  app.openapi(detachRoute, (async (c) => {
    const entityType = c.req.param('entityType')!; const entityId = c.req.param('entityId')!; const fileId = c.req.param('fileId')!
    await attachmentsService.detachEntityFile(entityType, entityId, fileId, getAuth(c))
    return c.body(null, 204)
  }) as OpenApiHandler)

  // GET /files/:fileId/entities
  const fileEntitiesRoute = createRoute({
    method: 'get',
    path: '/files/{fileId}/entities',
    tags: ['Attachments'],
    summary: 'List entities attached to a file',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    request: { params: fileIdParams },
    responses: {
      200: { description: 'Entities', content: { 'application/json': { schema: z.array(anyObject) } } }
    }
  })
  app.openapi(fileEntitiesRoute, (async (c) => {
    const fileId = c.req.param('fileId')!
    const entityType = c.req.query('entityType')
    return c.json(await attachmentsService.getEntitiesForFile(fileId, entityType))
  }) as OpenApiHandler)

  return app
}
